import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { BY_SYMBOL, elementColor } from '../elements';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueIsActive,
  DARK_INK,
  NowYouTry,
  usePrefersReducedMotion,
  VizCard,
  VIZ_BTN_GHOST,
  type VizCue,
} from '../vizShared';

// BondFormation — animates TWO atoms forming a bond, the most-requested concept
// visual. Both atoms render as orbiting-electron shells (valence electrons spin
// continuously). A single `progress` (0..1) drives the timeline:
//   approach → transfer (ionic) / share (covalent) → bonded.
//
// IONIC: the metal's valence electron(s) visibly TRANSFER to the nonmetal; both
// pick up + / − charges and the nonmetal completes its octet; they snap together
// and the compound formula appears (e.g. NaCl).
//
// COVALENT: the atoms approach and SHARE a pair in the overlap region (the shared
// pair oscillates); the bond + molecule formula appear (e.g. HCl, O₂, N₂).
//
// cue: { progress? → scrub the timeline 0..1; play? → gate the self-demo loop }.
// With no cue it runs a looping self-demo (approach→bond→hold→reset) plus a
// manual replay control and a "Now you try" handoff on interaction. Honors
// prefers-reduced-motion by rendering the final bonded state instantly.
//
// config: { kind: 'ionic' | 'covalent', atoms: [string, string], title?, cue_steps? }

export interface BondFormationConfig {
  kind?: 'ionic' | 'covalent';
  atoms?: [string, string] | string[];
  title?: string;
  /**
   * Either the standard narration cue track (`{highlight, step, at}[]`, which
   * this widget drives off `cue.progress` and otherwise ignores) OR an author's
   * per-phase caption list (`string[]`, [approach, transfer/share, bonded]).
   * Only string entries are used as captions — objects are ignored (never
   * rendered as a child, which previously threw).
   */
  cue_steps?: string[] | Array<{ highlight?: unknown; step?: number; at?: number }>;
}

interface Props {
  config?: BondFormationConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ── valence chemistry ──────────────────────────────────────────────────────
// Valence electrons for a main-group element, derived from its periodic group.
// Groups 1-2 → 1-2; groups 13-18 → group-10 (so 17 → 7, 18 → 8). Transition /
// f-block / unknown elements have no simple main-group valence: return null and
// the widget renders a best-effort generic atom instead of crashing.
function valenceOf(symbol: string): number | null {
  if (symbol === 'H') return 1;
  if (symbol === 'He') return 2;
  const info = BY_SYMBOL[symbol];
  if (!info || info.group == null) return null;
  const g = info.group;
  if (g === 1 || g === 2) return g;
  if (g >= 13 && g <= 18) return g - 10;
  return null; // d-block (3-12): no teaching main-group valence
}

type Role = 'metal' | 'nonmetal' | 'neutral';

// How many electrons this atom needs/gives toward an octet (duet for H/He shell).
function octetTarget(symbol: string): number {
  return symbol === 'H' || symbol === 'He' ? 2 : 8;
}

// Subscript a count for a formula (1 → '', 2 → '₂', …).
const SUBS = '₀₁₂₃₄₅₆₇₈₉';
function sub(n: number): string {
  if (n <= 1) return '';
  return String(n)
    .split('')
    .map((d) => SUBS[Number(d)] ?? d)
    .join('');
}

// Charge superscript (e.g. +1 → '⁺', -2 → '²⁻').
function chargeSup(c: number): string {
  if (c === 0) return '';
  const mag = Math.abs(c) === 1 ? '' : String(Math.abs(c));
  return `${mag}${c > 0 ? '⁺' : '⁻'}`;
}

interface Plan {
  kind: 'ionic' | 'covalent';
  a: string;
  b: string;
  vA: number; // valence electrons of A
  vB: number;
  // Ionic: metal donates `transfer` electrons to nonmetal.
  metalIdx: 0 | 1; // which atom is the metal (donor)
  transfer: number;
  // Covalent: number of SHARED pairs (1 single, 2 double, 3 triple).
  sharedPairs: number;
  // Resulting formula + per-ion charges (ionic) for labels.
  formula: string;
  chargeA: number;
  chargeB: number;
  // Was a clean main-group plan derivable? (false ⇒ best-effort generic render)
  resolved: boolean;
  note: string;
}

// Greatest common divisor for reducing ionic subscripts (e.g. Ca²⁺ + O²⁻ → CaO).
function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

// Build the bonding plan from the two symbols + requested kind. Handles the
// common teaching pairs and falls back gracefully for anything exotic.
function buildPlan(rawA: string, rawB: string, kind: 'ionic' | 'covalent'): Plan {
  const a = rawA;
  const b = rawB;
  const vAraw = valenceOf(a);
  const vBraw = valenceOf(b);
  const resolved = vAraw != null && vBraw != null;
  const vA = vAraw ?? 4;
  const vB = vBraw ?? 4;

  if (kind === 'ionic') {
    // The metal (lower valence / more metallic) donates; nonmetal accepts.
    // Metal donates its valence electrons; nonmetal needs (target − valence).
    const aIsMetal = vA <= vB;
    const metalIdx: 0 | 1 = aIsMetal ? 0 : 1;
    const metalV = aIsMetal ? vA : vB;
    const nmSym = aIsMetal ? b : a;
    const nmV = aIsMetal ? vB : vA;
    const need = octetTarget(nmSym) - nmV; // electrons the nonmetal accepts

    // Cation charge = metal valence; anion charge = -need.
    const cationCharge = metalV;
    const anionCharge = -need;
    // Balance the formula: counts inversely proportional to charges, reduced.
    const cMag = Math.abs(cationCharge) || 1;
    const aMag = Math.abs(anionCharge) || 1;
    const g = gcd(cMag, aMag);
    const nCation = aMag / g;
    const nAnion = cMag / g;

    const metalSym = aIsMetal ? a : b;
    const formula = `${metalSym}${sub(nCation)}${nmSym}${sub(nAnion)}`;
    // Per-atom charge labels in A/B order.
    const chargeA = aIsMetal ? cationCharge : anionCharge;
    const chargeB = aIsMetal ? anionCharge : cationCharge;

    return {
      kind: 'ionic',
      a,
      b,
      vA,
      vB,
      metalIdx,
      transfer: Math.min(metalV, need || metalV),
      sharedPairs: 0,
      formula,
      chargeA,
      chargeB,
      resolved,
      note: resolved
        ? `${metalSym} donates ${metalV} e⁻ → ${nmSym} completes its octet`
        : 'Electron transfer (best-effort: non-main-group element)',
    };
  }

  // Covalent: shared pairs = electrons each needs to fill its shell, limited by
  // the smaller deficit (and capped at a triple bond for the teaching cases).
  const needA = Math.max(0, octetTarget(a) - vA);
  const needB = Math.max(0, octetTarget(b) - vB);
  let sharedPairs = Math.max(1, Math.min(needA, needB));
  sharedPairs = Math.min(3, sharedPairs);
  const formula =
    a === b ? `${a}${sub(2)}` : `${a}${b}`; // diatomic vs simple AB

  return {
    kind: 'covalent',
    a,
    b,
    vA,
    vB,
    metalIdx: 0,
    transfer: 0,
    sharedPairs,
    formula,
    chargeA: 0,
    chargeB: 0,
    resolved,
    note: resolved
      ? `${sharedPairs === 1 ? 'single' : sharedPairs === 2 ? 'double' : 'triple'} bond · ${sharedPairs} shared pair${sharedPairs > 1 ? 's' : ''}`
      : 'Shared pair (best-effort: non-main-group element)',
  };
}

// ── geometry ────────────────────────────────────────────────────────────────
const W = 360;
const H = 220;
const CY = 104;
const SHELL_R = 34; // valence-shell radius
const NUC_R = 16;
const APART = 78; // half-distance between atom centers when fully apart
const BONDED = 40; // half-distance when bonded

const NUCLEUS_RED = '#E0564B';
const ELECTRON_GOLD = '#F2AF3F';

// Smoothstep ease for the timeline phases.
function smooth(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

// Place `count` electrons evenly on a shell, with a spin offset.
function shellPoints(cx: number, cy: number, r: number, count: number, spin: number) {
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i++) {
    const a = spin + (i / Math.max(1, count)) * Math.PI * 2;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}

export default function BondFormation({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const cueProgress = cue?.progress;

  // Resolve config → plan (memoized; never throws on bad input).
  const kind: 'ionic' | 'covalent' = config.kind === 'covalent' ? 'covalent' : 'ionic';
  const atoms = Array.isArray(config.atoms) ? config.atoms : [];
  const symA = typeof atoms[0] === 'string' && atoms[0] ? atoms[0] : kind === 'ionic' ? 'Na' : 'H';
  const symB = typeof atoms[1] === 'string' && atoms[1] ? atoms[1] : kind === 'ionic' ? 'Cl' : 'Cl';
  const plan = useMemo(() => buildPlan(symA, symB, kind), [symA, symB, kind]);

  // progress 0..1 drives the whole timeline. spin drives continuous orbiting.
  const [progress, setProgress] = useState(reduced || cueProgress != null ? 1 : 0);
  const [spin, setSpin] = useState(0);
  const [demoDone, setDemoDone] = useState(reduced);
  const interactedRef = useRef(false);

  // self-demo loop state: phase clock (seconds). Runs only with no cue + motion.
  const selfDemo = !reduced && !hasCue;
  const phaseRef = useRef(0);
  const runningRef = useRef(selfDemo);

  // Continuous electron spin (always, unless reduced motion).
  useAnimationFrame((dt) => {
    if (!reduced) setSpin((s) => s + dt * 1.6);
  }, !reduced);

  // Self-demo timeline: approach (0→1 over ~2s) → hold bonded (~1.6s) → reset.
  // cue.play === false pauses the loop; an interaction stops it for good.
  useAnimationFrame(
    (dt) => {
      if (!runningRef.current) return;
      if (cue?.play === false) return;
      phaseRef.current += dt;
      const t = phaseRef.current;
      const APPROACH = 2.0;
      const HOLD = 1.6;
      const RESET = 0.5;
      const CYCLE = APPROACH + HOLD + RESET;
      const local = t % CYCLE;
      if (local < APPROACH) {
        setProgress(smooth(local / APPROACH));
      } else if (local < APPROACH + HOLD) {
        setProgress(1);
        if (!demoDone) setDemoDone(true);
      } else {
        setProgress(1 - smooth((local - APPROACH - HOLD) / RESET));
      }
    },
    selfDemo,
  );

  // Cue.progress scrubs the timeline directly (supersedes the self-demo loop).
  useEffect(() => {
    if (cueProgress == null) return;
    runningRef.current = false;
    setProgress(Math.max(0, Math.min(1, cueProgress)));
  }, [cueProgress]);

  // Replay is the only interactive control; it re-arms the self-demo loop and
  // signals completion (the "Now you try" handoff) on the first interaction.
  const replay = () => {
    if (reduced) return;
    phaseRef.current = 0;
    runningRef.current = true;
    setDemoDone(false);
    setProgress(0);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'bond_formation', kind: plan.kind, atoms: [plan.a, plan.b] });
    }
  };

  // ── derived layout from progress ──────────────────────────────────────────
  const p = progress;
  const half = APART + (BONDED - APART) * p; // centers move together
  const cxA = W / 2 - half;
  const cxB = W / 2 + half;

  // Phase booleans for staged labels.
  const transferring = p > 0.45; // electrons in flight / shared
  const bonded = p > 0.92;

  // Effective valence shown on each atom (ionic: changes as e⁻ transfer).
  const isIonic = plan.kind === 'ionic';
  const metalIsA = plan.metalIdx === 0;

  // For ionic: animate the donated electron(s) from metal shell → nonmetal shell.
  // Compute current valence counts to draw.
  let drawValA = plan.vA;
  let drawValB = plan.vB;
  if (isIonic && transferring) {
    const moved = bonded ? plan.transfer : 0;
    if (metalIsA) {
      drawValA = plan.vA - moved;
      drawValB = plan.vB + moved;
    } else {
      drawValB = plan.vB - moved;
      drawValA = plan.vA + moved;
    }
  }

  // Charge labels appear only once bonded (ionic).
  const showChargeA = isIonic && bonded && plan.chargeA !== 0;
  const showChargeB = isIonic && bonded && plan.chargeB !== 0;

  // Shared-pair oscillation (covalent): the pair drifts left↔right in the overlap.
  const overlapX = (cxA + cxB) / 2;
  const osc = Math.sin(spin * 1.5) * 10;

  // Electron(s) in transit (ionic): interpolate from metal shell edge to nonmetal.
  const transitElectrons: Array<{ x: number; y: number }> = [];
  if (isIonic && p > 0.45 && p < 0.95) {
    const tt = smooth((p - 0.45) / 0.5);
    const fromX = (metalIsA ? cxA : cxB) + (metalIsA ? SHELL_R : -SHELL_R);
    const toX = (metalIsA ? cxB : cxA) + (metalIsA ? -SHELL_R : SHELL_R);
    for (let i = 0; i < plan.transfer; i++) {
      const wob = (i - (plan.transfer - 1) / 2) * 9;
      transitElectrons.push({
        x: fromX + (toX - fromX) * tt,
        y: CY + Math.sin(tt * Math.PI) * (18 + wob),
      });
    }
  }

  const trans = reduced ? undefined : 'opacity 300ms ease-out';

  // Phase caption (author override via cue_steps, else sensible defaults).
  // ONLY string entries are captions; the standard {highlight,step,at} cue track
  // is ignored here (this widget syncs off cue.progress), and must never be
  // rendered as a React child.
  const captions =
    Array.isArray(config.cue_steps) && config.cue_steps.every((c) => typeof c === 'string')
      ? (config.cue_steps as string[])
      : null;
  const phaseCaption = (() => {
    const idx = p < 0.4 ? 0 : p < 0.92 ? 1 : 2;
    if (captions) return captions[Math.min(idx, captions.length - 1)];
    if (idx === 0) return 'Atoms approach — count the valence electrons';
    if (idx === 1) return isIonic ? 'Electron transfer in progress…' : 'Sharing a pair in the overlap…';
    return isIonic ? 'Ions snap together' : 'Bonded molecule';
  })();

  // Before → after octet readout.
  const targetA = octetTarget(plan.a);
  const targetB = octetTarget(plan.b);

  return (
    <VizCard
      eyebrow={`Bonding · ${isIonic ? 'Ionic' : 'Covalent'} bond`}
      title={config.title ?? `${plan.a} + ${plan.b} → ${plan.formula}`}
      subtitle={
        isIonic
          ? 'Metal donates electron(s); nonmetal completes its octet'
          : 'Atoms share electron pair(s) to complete their octets'
      }
      reducedMotion={reduced}
      hint="Plays itself on load · Replay to watch again · scrubbed by narration when in a lecture"
    >
      <div className="flex w-full flex-col gap-3 lg:flex-row">
        {/* Stage */}
        <div className="relative flex-shrink-0">
          <NowYouTry show={demoDone && !hasCue && !reduced} />
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full max-w-[360px] rounded-md border border-ws-border bg-ws-surface-2"
            role="img"
            aria-label={`${isIonic ? 'Ionic' : 'Covalent'} bond formation: ${plan.a} and ${plan.b} forming ${plan.formula}`}
          >
            {/* Bond / overlap rendered under the atoms */}
            {!isIonic && transferring && (
              <g style={trans ? { transition: trans } : undefined} opacity={transferring ? 1 : 0}>
                {/* bond line(s) between the two atoms */}
                {Array.from({ length: plan.sharedPairs }).map((_, i) => {
                  const off = plan.sharedPairs === 1 ? 0 : (i - (plan.sharedPairs - 1) / 2) * 7;
                  return (
                    <line
                      key={i}
                      x1={cxA + NUC_R}
                      y1={CY + off}
                      x2={cxB - NUC_R}
                      y2={CY + off}
                      stroke="var(--ws-ink-3)"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            )}

            {/* Atom A */}
            <Atom
              cx={cxA}
              cy={CY}
              symbol={plan.a}
              valence={drawValA}
              spin={spin}
              reduced={reduced}
            />
            {/* Atom B */}
            <Atom
              cx={cxB}
              cy={CY}
              symbol={plan.b}
              valence={drawValB}
              spin={-spin}
              reduced={reduced}
            />

            {/* Ionic: electrons in transit */}
            {transitElectrons.map((e, i) => (
              <circle key={`tr${i}`} cx={e.x} cy={e.y} r="5" fill={ELECTRON_GOLD} stroke="#fff" strokeWidth="1" />
            ))}

            {/* Covalent: the shared pair(s) oscillating in the overlap */}
            {!isIonic &&
              transferring &&
              Array.from({ length: plan.sharedPairs }).map((_, i) => {
                const off = plan.sharedPairs === 1 ? 0 : (i - (plan.sharedPairs - 1) / 2) * 12;
                return (
                  <g key={`sp${i}`}>
                    <circle cx={overlapX + osc} cy={CY + off - 4} r="4.5" fill={ELECTRON_GOLD} stroke="#fff" strokeWidth="1" />
                    <circle cx={overlapX - osc} cy={CY + off + 4} r="4.5" fill={ELECTRON_GOLD} stroke="#fff" strokeWidth="1" />
                  </g>
                );
              })}

            {/* Charge badges (ionic, bonded) */}
            {showChargeA && (
              <ChargeBadge cx={cxA} cy={CY} charge={plan.chargeA} />
            )}
            {showChargeB && (
              <ChargeBadge cx={cxB} cy={CY} charge={plan.chargeB} />
            )}

            {/* Resulting formula once bonded */}
            <text
              x={W / 2}
              y={H - 12}
              textAnchor="middle"
              fontSize="16"
              fontWeight="800"
              fill="var(--ws-accent)"
              style={trans ? { transition: trans } : undefined}
              opacity={bonded ? 1 : 0}
            >
              {plan.formula}
            </text>
          </svg>

          {/* Phase caption */}
          <p className="mt-1 min-h-[1.25rem] text-center text-xs text-ws-ink-2">{phaseCaption}</p>

          {/* Legend + replay */}
          <div className="mt-1 flex items-center justify-center gap-4 text-[11px] text-ws-ink-2">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: NUCLEUS_RED }} />
              Nucleus (+)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ELECTRON_GOLD }} />
              Valence e⁻
            </span>
            {!reduced && (
              <button type="button" onClick={replay} className={`${VIZ_BTN_GHOST} !px-2 !py-0.5`} aria-label="Replay animation">
                <RotateCcw size={12} /> Replay
              </button>
            )}
          </div>
        </div>

        {/* Readout */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-sm border border-ws-border bg-ws-tile p-2 text-xs text-ws-ink-2">
            <div className="col-span-2 font-semibold text-ws-ink">{isIonic ? 'Ionic bond' : 'Covalent bond'}</div>
            <div>
              <dt className="inline text-ws-ink-3">{plan.a}: </dt>
              <dd className="inline font-medium tabular-nums">{plan.vA} valence e⁻</dd>
            </div>
            <div>
              <dt className="inline text-ws-ink-3">{plan.b}: </dt>
              <dd className="inline font-medium tabular-nums">{plan.vB} valence e⁻</dd>
            </div>
            {isIonic ? (
              <>
                <div className="col-span-2">
                  <dt className="inline text-ws-ink-3">Transfer: </dt>
                  <dd className="inline font-medium">
                    {plan.transfer} e⁻ {metalIsA ? `${plan.a} → ${plan.b}` : `${plan.b} → ${plan.a}`}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="inline text-ws-ink-3">Ions: </dt>
                  <dd className="inline font-medium">
                    {plan.a}
                    <sup>{chargeSup(plan.chargeA) || '0'}</sup> + {plan.b}
                    <sup>{chargeSup(plan.chargeB) || '0'}</sup>
                  </dd>
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <dt className="inline text-ws-ink-3">Shared: </dt>
                <dd className="inline font-medium">
                  {plan.sharedPairs} pair{plan.sharedPairs > 1 ? 's' : ''} (
                  {plan.sharedPairs === 1 ? 'single' : plan.sharedPairs === 2 ? 'double' : 'triple'} bond)
                </dd>
              </div>
            )}
            <div className="col-span-2">
              <dt className="inline text-ws-ink-3">Result: </dt>
              <dd className="inline font-semibold text-ws-ink">{plan.formula}</dd>
            </div>
          </dl>

          {/* Before → after octet count */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-sm border border-ws-border bg-ws-tile p-2 text-xs text-ws-ink-2">
            <div className="col-span-2 text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
              Valence before → after
            </div>
            <div>
              <dt className="inline text-ws-ink-3">{plan.a}: </dt>
              <dd className="inline font-medium tabular-nums">
                {plan.vA} → {afterCount(plan, 'a', targetA)}
              </dd>
            </div>
            <div>
              <dt className="inline text-ws-ink-3">{plan.b}: </dt>
              <dd className="inline font-medium tabular-nums">
                {plan.vB} → {afterCount(plan, 'b', targetB)}
              </dd>
            </div>
          </dl>

          <p className="text-[11px] leading-snug text-ws-ink-3">
            {plan.note}
            {!plan.resolved && ' — exact electron counts simplified for this element.'}
          </p>
        </div>
      </div>
    </VizCard>
  );
}

// Effective shell electrons each atom ends with (both reach their octet/duet in
// the resolved teaching cases).
function afterCount(plan: Plan, which: 'a' | 'b', target: number): number {
  if (!plan.resolved) return target;
  // Ionic: both ions reach their octet/duet (metal exposes the full shell below,
  // nonmetal fills to its octet).
  if (plan.kind === 'ionic') return target;
  // Covalent: shared pairs count toward both atoms' shells.
  const v = which === 'a' ? plan.vA : plan.vB;
  return Math.min(target, v + plan.sharedPairs);
}

// One orbiting-electron atom: nucleus (proton red) + valence shell with spinning
// gold electrons, labeled symbol + valence count.
function Atom({
  cx,
  cy,
  symbol,
  valence,
  spin,
  reduced,
}: {
  cx: number;
  cy: number;
  symbol: string;
  valence: number;
  spin: number;
  reduced: boolean;
}) {
  const pts = shellPoints(cx, cy, SHELL_R, valence, reduced ? 0 : spin);
  return (
    <g>
      {/* valence shell ring */}
      <circle cx={cx} cy={cy} r={SHELL_R} fill="none" stroke="var(--ws-border)" strokeWidth="1" />
      {/* nucleus core colored by element, with a proton-red ring */}
      <circle cx={cx} cy={cy} r={NUC_R} fill={elementColor(symbol)} stroke={NUCLEUS_RED} strokeWidth="2" />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize="13" fontWeight="800" style={{ fill: DARK_INK }}>
        {symbol}
      </text>
      {/* valence count chip under the atom */}
      <text x={cx} y={cy + SHELL_R + 14} textAnchor="middle" fontSize="9" fill="var(--ws-ink-3)">
        {valence} e⁻
      </text>
      {/* orbiting valence electrons */}
      {pts.map((pt, i) => (
        <circle key={i} cx={pt.x} cy={pt.y} r="4.5" fill={ELECTRON_GOLD} stroke="#fff" strokeWidth="1" />
      ))}
    </g>
  );
}

// A small +/− charge badge attached to the top-right of an ion.
function ChargeBadge({ cx, cy, charge }: { cx: number; cy: number; charge: number }) {
  const positive = charge > 0;
  return (
    <g>
      <circle
        cx={cx + SHELL_R - 6}
        cy={cy - SHELL_R + 6}
        r="9"
        fill={positive ? 'var(--ws-danger)' : 'var(--ws-accent)'}
        stroke="#fff"
        strokeWidth="1.5"
      />
      <text
        x={cx + SHELL_R - 6}
        y={cy - SHELL_R + 10}
        textAnchor="middle"
        fontSize="11"
        fontWeight="800"
        fill="#fff"
      >
        {chargeSup(charge).replace('⁺', '+').replace('⁻', '−')}
      </text>
    </g>
  );
}
